import type { AppFilterFn } from "./table-features";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse "operator:value" encoding. Plain values (no prefix) return operator "" */
function parseFilterValue(filterValue: string): {
  operator: string;
  value: string;
} {
  const prefixMatch = /^(!=|>=|<=|contains|not_contains|between|>|<):(.*)$/.exec(filterValue);
  if (prefixMatch) {
    return { operator: prefixMatch[1], value: prefixMatch[2] };
  }
  return { operator: "", value: filterValue };
}

// ---------------------------------------------------------------------------
// jqlFilterFn – generic filter handling all JQL operators
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic filter must work with any row type
export const jqlFilterFn: AppFilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId);
  if (cellValue == null) return false;

  const { operator, value } = parseFilterValue(String(filterValue));

  // Between operator: "between:min|max"
  if (operator === "between") {
    const [minStr, maxStr] = value.split("|");
    if (!minStr || !maxStr) return false;

    const cellNum = Number(cellValue);
    const minNum = Number(minStr);
    const maxNum = Number(maxStr);
    if (!Number.isNaN(cellNum) && !Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
      return cellNum >= minNum && cellNum <= maxNum;
    }

    // Date between
    const cellDate = new Date(String(cellValue)).getTime();
    const minDate = new Date(minStr).getTime();
    const maxDate = new Date(maxStr).getTime();
    if (!Number.isNaN(cellDate) && !Number.isNaN(minDate) && !Number.isNaN(maxDate)) {
      return cellDate >= minDate && cellDate <= maxDate + 86400000;
    }
    return false;
  }

  // Numeric / date operators
  if (operator === ">" || operator === "<" || operator === ">=" || operator === "<=") {
    let cellCmp: number;
    let filterCmp: number;

    const cellNum = Number(cellValue);
    const filterNum = Number(value);
    if (!Number.isNaN(cellNum) && !Number.isNaN(filterNum)) {
      cellCmp = cellNum;
      filterCmp = filterNum;
    } else {
      const cellDate = new Date(String(cellValue)).getTime();
      const filterDate = new Date(value).getTime();
      if (Number.isNaN(cellDate) || Number.isNaN(filterDate)) return false;
      cellCmp = cellDate;
      filterCmp = filterDate;
    }

    switch (operator) {
      case ">":
        return cellCmp > filterCmp;
      case "<":
        return cellCmp < filterCmp;
      case ">=":
        return cellCmp >= filterCmp;
      case "<=":
        return cellCmp <= filterCmp;
    }
  }

  const cellStr = String(cellValue).toLowerCase();
  const filterStr = value.toLowerCase();

  // Date-aware != : compare at day level when filter is a YYYY-MM-DD date
  if (operator === "!=") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const cellDate = new Date(String(cellValue));
      if (!Number.isNaN(cellDate.getTime())) {
        return cellDate.toISOString().slice(0, 10) !== value;
      }
    }
    return cellStr !== filterStr;
  }

  if (operator === "not_contains") {
    return !cellStr.includes(filterStr);
  }

  if (operator === "contains") {
    return cellStr.includes(filterStr);
  }

  // "=" or no operator — date-aware day-level match when filter is YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const cellDate = new Date(String(cellValue));
    if (!Number.isNaN(cellDate.getTime())) {
      return cellDate.toISOString().slice(0, 10) === value;
    }
  }
  return cellStr.includes(filterStr);
};
jqlFilterFn.autoRemove = (val: unknown) => !val;

// ---------------------------------------------------------------------------
// enumFilterFn – exact match for enum columns (status, deal type, etc.)
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const enumFilterFn: AppFilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId);
  if (cellValue == null) return false;

  const { operator, value } = parseFilterValue(String(filterValue));
  const cellStr = String(cellValue).toLowerCase();

  // Multi-select: comma-separated values
  if (value.includes(",")) {
    const values = value.split(",").map((v) => v.trim().toLowerCase());
    return operator === "!=" ? !values.includes(cellStr) : values.includes(cellStr);
  }

  const filterStr = value.toLowerCase();
  if (operator === "!=") return cellStr !== filterStr;
  return cellStr === filterStr;
};
enumFilterFn.autoRemove = (val: unknown) => !val;

// ---------------------------------------------------------------------------
// textFilterFn – case-insensitive contains for text columns
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const textFilterFn: AppFilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId);
  if (cellValue == null) return false;

  const { operator, value } = parseFilterValue(String(filterValue));

  const cellStr = String(cellValue).toLowerCase();
  const filterStr = value.toLowerCase();

  if (operator === "!=") {
    return cellStr !== filterStr;
  }

  if (operator === "not_contains") {
    return !cellStr.includes(filterStr);
  }

  // "contains" or default → substring match
  return cellStr.includes(filterStr);
};
textFilterFn.autoRemove = (val: unknown) => !val;

// ---------------------------------------------------------------------------
// multiSelectFilterFn – matches when any selected value appears in the cell's array
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const multiSelectFilterFn: AppFilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId);
  if (cellValue == null) return false;

  const { operator, value } = parseFilterValue(String(filterValue));

  // Cell value is a comma-separated join of the array values
  const cellValues = String(cellValue).split(", ").map((v) => v.toLowerCase());

  if (value.includes(",")) {
    const filterValues = value.split(",").map((v) => v.trim().toLowerCase());
    return operator === "!="
      ? !filterValues.some((fv) => cellValues.includes(fv))
      : filterValues.some((fv) => cellValues.includes(fv));
  }

  const filterStr = value.toLowerCase();
  return operator === "!="
    ? !cellValues.includes(filterStr)
    : cellValues.includes(filterStr);
};
multiSelectFilterFn.autoRemove = (val: unknown) => !val;

// ---------------------------------------------------------------------------
// datePresetFilterFn – handles date presets (today, this_week, etc.) and custom dates
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const datePresetFilterFn: AppFilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId);
  if (cellValue == null) return false;

  const cellDate = new Date(String(cellValue));
  if (Number.isNaN(cellDate.getTime())) return false;

  const { operator, value } = parseFilterValue(String(filterValue));

  // Handle operator-based date filtering
  if (operator === "<") {
    const filterDate = new Date(value);
    return !Number.isNaN(filterDate.getTime()) && cellDate < filterDate;
  }
  if (operator === ">") {
    const filterDate = new Date(value);
    if (Number.isNaN(filterDate.getTime())) return false;
    return cellDate >= new Date(filterDate.getTime() + 86400000);
  }
  if (operator === "between") {
    const [fromStr, toStr] = value.split("|");
    if (!fromStr || !toStr) return false;
    const fromDate = new Date(fromStr);
    const toDate = new Date(toStr);
    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) return false;
    return cellDate >= fromDate && cellDate < new Date(toDate.getTime() + 86400000);
  }
  if (operator === "!=") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return cellDate.toISOString().slice(0, 10) !== value;
    }
    return true;
  }

  // Default: preset-based filtering (no operator or "is")
  const rawValues = operator ? value : String(filterValue);
  const values = rawValues.split(",").map((v) => v.trim());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const DAY_MS = 86400000;

  return values.some((preset) => {
    switch (preset) {
      case "today":
        return cellDate >= today && cellDate < new Date(today.getTime() + DAY_MS);
      case "this_week": {
        const day = today.getDay();
        const monday = new Date(today.getTime() - (day === 0 ? 6 : day - 1) * DAY_MS);
        const sunday = new Date(monday.getTime() + 7 * DAY_MS);
        return cellDate >= monday && cellDate < sunday;
      }
      case "this_month": {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        return cellDate >= monthStart && cellDate < monthEnd;
      }
      case "overdue":
        return cellDate < today;
      default:
        if (/^\d{4}-\d{2}-\d{2}$/.test(preset)) {
          return cellDate.toISOString().slice(0, 10) === preset;
        }
        return false;
    }
  });
};
datePresetFilterFn.autoRemove = (val: unknown) => !val;

// ---------------------------------------------------------------------------
// globalSearchFn – case-insensitive substring match for global search input
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const globalSearchFn: AppFilterFn<any> = (row, columnId, filterValue) => {
  const search = String(filterValue).toLowerCase().trim();
  if (!search) return true;
  const cellValue = row.getValue(columnId);
  if (cellValue == null) return false;
  return String(cellValue).toLowerCase().includes(search);
};
globalSearchFn.autoRemove = (val: unknown) => !val || String(val).trim() === "";
